import { ChangeDetectorRef, Component, forwardRef, inject, Input, OnChanges, OnInit, Provider, SimpleChanges } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GuidEmpty, TableCellDirective, TableHeader } from 'mbs-ui-kit';
import { PlanMode } from 'mbs-ui-shared@models/PlanTypes.enum';
import { ComputerItem, ComputerToRestoreStepValue } from 'wizards/models/selected-computers-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ParamsForRCRestoreMethods, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

import { ListSelectComponent } from 'mbs-ui-kit';

import { ReactiveFormsModule } from '@angular/forms';

const SelectComputerStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => SelectComputerStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-select-computer-step',
  templateUrl: './select-computer-step.component.html',
  providers: [SelectComputerStepValueAccessor],
  imports: [ListSelectComponent, ReactiveFormsModule, TableCellDirective]
})
export class SelectComputerStepComponent extends StepBase<ComputerToRestoreStepValue> implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  mainService: RemoteManagementWizardsService;
  private stepService = inject(WizardStepsService);

  @Input() storageId: string;

  public headers: TableHeader[] = [{ name: '', gridColSize: '10fr', class: '-stretch' }];
  public computers: ComputerItem[] = [];
  public computerName = '';

  private firstLoad = true;

  get previewMode(): boolean {
    const form = this.mainService.initialFormData;
    return !this.isEdit && this.isRestoreIbb && form?.computerToRestoreStep && form.whereBackupStep?.ConnectionID === this.storageId;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      computerName: new FormControl('', [Validators.required]),
      isCurrent: new FormControl(true)
    });

    this.initFormEvents();
  }

  onStepFormChange(value: ComputerToRestoreStepValue): void {
    if (value?.computerName) this.computerName = value.computerName;
    if (this.stepForm.dirty || this.stepForm.touched) this.value = { ...value, valid: this.stepForm.valid };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.storageId && this.stepForm && (!changes.storageId.currentValue || changes.storageId.currentValue === GuidEmpty)) {
      this.resetComputers();
    }

    if (
      changes.stepFocused?.currentValue &&
      !changes.stepFocused.previousValue &&
      !this.value?.computerName &&
      (this.isCreate || (this.isUnix && !this.isNetBuild))
    ) {
      this.setCurrentComputer();
    }

    if (changes.storageId && changes.storageId.currentValue && changes.storageId.currentValue !== GuidEmpty) {
      this.resetComputersIfCreateOrNotFirstLoad();

      if (this.previewMode) this.setSelectedComputer();
      else this.loadPrefixes();
    }
  }

  clickHandler(): void {
    if (this.stepForm.dirty) return;

    this.stepForm.markAsDirty();
    this.stepForm.updateValueAndValidity();
  }

  changeSelectedHandler(selectedComputers: string[]): void {
    if (!selectedComputers?.length) {
      this.stepForm.get('computerName').setValue('');
      return this.cdr.detectChanges();
    }

    const name = selectedComputers[0];
    const comp = this.computers.find((c: ComputerItem) => c.name === name);

    if (comp) {
      this.stepForm.get('isCurrent').setValue(comp.isCurrent);
      this.stepForm.get('computerName').setValue(comp.name);
    }

    this.cdr.detectChanges();
  }

  private setSelectedComputer(): void {
    this.loadInfo.emit({ loading: true });
    const stepData = this.mainService.initialFormData.computerToRestoreStep;
    this.computers = [{ name: stepData.computerName, isCurrent: false, disabled: true }];
    this.updateSelectedBunchFromName();
    this.loadInfo.emit({ loading: false });
  }

  private loadPrefixes(): void {
    const treeParams: ParamsForRCRestoreMethods = {
      agentType: 'backup',
      commandType: 'GetRestoreSourcePrefix',
      params: { ConnectionId: this.storageId }
    };

    if (this.mainService.sessionId) treeParams.params.SessionId = this.mainService.sessionId;

    this.loadInfo.emit({ loading: true });
    this.stepService
      .getRemoteCommandData(treeParams, this.mainService.hid)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (computers) => {
          if (computers?.data?.length) {
            this.computers =
              this.isUnix && !this.isNetBuild
                ? computers.data.map((p) => {
                    p.disabled = true;
                    return p;
                  })
                : computers.data;
            this.updateSelectedBunchFromName();
            this.mainService.currentPrefix = computers.data.find((c: ComputerItem) => c.isCurrent)?.name || '';
          }
          this.loadInfo.emit({ loading: false });
        },
        error: () => {
          this.loadInfo.emit({ loading: false });
          this.stepForm.get('isCurrent').setValue(false);
          this.stepForm.get('computerName').setValue('');
          this.computers = [];
        }
      });
  }

  private setCurrentComputer(): void {
    const currentFound = this.computers.find((c: ComputerItem) => c.isCurrent);

    if (!currentFound) return;

    this.stepForm.get('isCurrent').setValue(true);
    this.stepForm.get('computerName').markAsTouched();
    this.stepForm.get('computerName').setValue(currentFound.name);
    this.cdr.detectChanges();
  }

  private resetComputersIfCreateOrNotFirstLoad(): void {
    if (this.mainService.mode !== PlanMode.edit || !this.firstLoad) {
      if (this.stepForm) this.stepForm.get('computerName').markAsTouched();
      return this.resetComputers();
    }

    this.firstLoad = false;
  }

  private resetComputers(): void {
    this.stepForm.get('isCurrent').setValue(false);
    this.stepForm.get('computerName').setValue('');
    this.computers = [];
  }

  private updateSelectedBunchFromName(): void {
    const found = this.computers.find(
      (c: ComputerItem) =>
        c.name === this.mainService.initialFormData?.computerToRestoreStep?.computerName ||
        c.name === this.computerName ||
        (this.isUnix && !this.isNetBuild && c.isCurrent)
    );

    if (found) {
      this.stepForm.get('isCurrent').setValue(found.isCurrent);
      this.stepForm.get('computerName').markAsTouched();
      this.stepForm.get('computerName').setValue(found.name);
      return this.cdr.detectChanges();
    }

    if (this.stepFocused && (this.isCreate || (this.isUnix && !this.isNetBuild))) this.setCurrentComputer();
  }
}
